import { Box, Text, SimpleGrid, Skeleton } from '@chakra-ui/react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import type { EventStats, ChartPoint, VolunteersChartPoint, PieDataItem } from './useVolunteersPage';
import { StatCard } from './components';
import { formatDuration } from '@/utils/volunteerHours';

interface Props {
  stats: EventStats;
  chartData: ChartPoint[];
  volunteersChartData: VolunteersChartPoint[];
  pieChartData: PieDataItem[];
  loading: boolean;
}

function ChartCard({ title, children, empty }: { title: string; children: React.ReactNode; empty?: boolean }) {
  return (
    <Box bg="volyx.bg" borderRadius="2xl" boxShadow="0 6px 18px rgba(0,0,0,0.12)" px="4" py="4" mb="4" minW={0}>
      <Text fontSize={{ base: 'sm', md: 'md' }} fontWeight="semibold" color="volyx.primary" mb="3" m="0">
        {title}
      </Text>
      {empty ? (
        <Box textAlign="center" py="6">
          <Text fontSize="sm" color="volyx.textSecondary">No data available</Text>
        </Box>
      ) : (
        children
      )}
    </Box>
  );
}

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export function VolunteersStats({ stats, chartData, volunteersChartData, pieChartData, loading }: Props) {
  const totalPieValue = pieChartData.reduce((s, d) => s + d.value, 0);

  if (loading) {
    return (
      <Box>
        <SimpleGrid columns={2} gap="3" mb="4">
          {[0, 1, 2, 3].map((k) => <Skeleton key={k} h="80px" borderRadius="xl" />)}
        </SimpleGrid>
        <Skeleton h="220px" borderRadius="xl" mb="4" />
        <Skeleton h="220px" borderRadius="xl" />
      </Box>
    );
  }

  return (
    <Box>
      <SimpleGrid columns={2} gap="3" mb="4">
        <StatCard label="Average Age" value={stats.averageAge > 0 ? String(stats.averageAge) : 'N/A'} loading={false} />
        <StatCard label="Hours / Volunteer" value={formatDuration(stats.averageHoursPerVolunteer)} loading={false} />
        <StatCard label="Total Volunteers" value={String(stats.totalVolunteers)} loading={false} />
        <StatCard label="Total Hours" value={formatDuration(stats.totalHours)} loading={false} />
      </SimpleGrid>

      {/* Hours chart */}
      <ChartCard title="Total hours of volunteering" empty={chartData.length === 0}>
        <Box h={{ base: '170px', md: '190px' }} minW={0}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="hoursGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FFD700" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#FFD700" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F5F5FA" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 9, fill: 'var(--chakra-colors-volyx-textSecondary)' }}
                tickMargin={6}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatDate}
              />
              <YAxis
                tick={{ fontSize: 9, fill: 'var(--chakra-colors-volyx-textSecondary)' }}
                tickMargin={6}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: '1px solid #E5E7EB',
                  fontSize: 11,
                  padding: '6px 8px',
                }}
                labelFormatter={(label) => formatDate(String(label))}
                formatter={(value) => [formatDuration(value as number), 'Hours']}
              />
              <Area type="monotone" dataKey="hours" stroke="#FFD700" fill="url(#hoursGrad)" strokeWidth={2.5} />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </ChartCard>

      {/* Volunteers chart */}
      <ChartCard title="Volunteers" empty={volunteersChartData.length === 0}>
        <Box h={{ base: '170px', md: '190px' }} minW={0}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <AreaChart data={volunteersChartData}>
              <defs>
                <linearGradient id="volsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F5F5FA" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 9, fill: 'var(--chakra-colors-volyx-textSecondary)' }}
                tickMargin={6}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatDate}
              />
              <YAxis
                tick={{ fontSize: 9, fill: 'var(--chakra-colors-volyx-textSecondary)' }}
                tickMargin={6}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: '1px solid #E5E7EB',
                  fontSize: 11,
                  padding: '6px 8px',
                }}
                labelFormatter={(label) => formatDate(String(label))}
                formatter={(value) => [value, 'Volunteers']}
              />
              <Area type="monotone" dataKey="count" stroke="#10B981" fill="url(#volsGrad)" strokeWidth={2.5} />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </ChartCard>

      {/* Department pie chart */}
      <ChartCard title="Volunteer distribution by department" empty={pieChartData.length === 0}>
        <Box display="flex" flexDirection={{ base: 'column', md: 'row' }} alignItems="center" gap="5">
          <Box w="160px" h="160px" flexShrink={0}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  dataKey="value"
                  nameKey="text"
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  stroke="none"
                >
                  {pieChartData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <text
                  x="50%"
                  y="50%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{ fontSize: 14, fontWeight: 'bold', fill: 'var(--chakra-colors-volyx-primary)' }}
                >
                  {totalPieValue}
                </text>
              </PieChart>
            </ResponsiveContainer>
          </Box>
          <Box flex="1" display="flex" flexDirection="column" gap="1.5">
            {pieChartData.map((item, i) => {
              const pct = totalPieValue ? Math.round((item.value / totalPieValue) * 100) : 0;
              return (
                <Box key={i} display="flex" alignItems="center" gap="2">
                  <Box w="8px" h="8px" borderRadius="full" bg={item.color} flexShrink={0} />
                  <Text fontSize="11px" color="volyx.primary" truncate m="0">
                    {item.text} — {item.value} ({pct}%)
                  </Text>
                </Box>
              );
            })}
          </Box>
        </Box>
      </ChartCard>
    </Box>
  );
}
